/**
 * The state and behaviour the two schedule drawers share (docs/DUPLICATION_AUDIT.md, B5):
 * ScheduleReportDrawer creates a schedule from a report page, ScheduleDrawer edits one on
 * the Scheduled page. The cards that render this state are in ScheduleFormCards.jsx; each
 * drawer keeps its own shell, its own extra fields and its own payload.
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { iAmHigherAuthority } from '../../../../libs.jsx';
// fetchUsers carries user_role_weight, which filterOptions.users does not.
import { fetchUsers } from '../../../users/api/usersApi.js';

const EMAIL_PATTERN = /^[a-z0-9._%+\-]+@[a-z0-9.\-]+\.[a-z]{2,}$/;

/** 'HH:MM' → seconds after midnight; empty is 9:00 AM. */
export function timeInputToSeconds(timeStr) {
    if (!timeStr) return 32400;
    const [h, m] = timeStr.split(':').map(Number);
    return (h * 3600) + (m * 60);
}

/** Seconds after midnight → 'HH:MM'; missing or unparseable is 9:00 AM. */
export function secondsToTimeInput(sec) {
    if (sec == null) return '09:00';
    const totalSec = parseInt(sec, 10);
    if (Number.isNaN(totalSec)) return '09:00';
    const h = Math.floor(totalSec / 3600);
    const m = Math.floor((totalSec % 3600) / 60);
    return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

const csvList = (s) => String(s || '').split(',').map((v) => v.trim()).filter(Boolean);

/**
 * Details, attachments and recipients. `load` sets every field at once: call it with
 * nothing for a new schedule (PDF Summary on, daily at 9:00 AM), or with a schedule row.
 */
export function useScheduleForm() {
    const [description, setDescription] = useState('');
    const [frequencyId, setFrequencyIdState] = useState(1);
    const [selectedDays, setSelectedDays] = useState(() => new Set());
    const [sendTime, setSendTime] = useState('09:00');
    const [pdfSummary, setPdfSummary] = useState(true);
    const [pdfDetails, setPdfDetails] = useState(false);
    const [csvDetails, setCsvDetails] = useState(false);
    const [emails, setEmails] = useState([]);
    const [newEmail, setNewEmailState] = useState('');
    const [emailError, setEmailError] = useState('');
    const [selectedUserIds, setSelectedUserIds] = useState(() => new Set());
    const [userSearch, setUserSearch] = useState('');

    const load = useCallback((row) => {
        setDescription(row?.description || '');
        setFrequencyIdState(row?.issue_freq_id || 1);
        setSelectedDays(new Set(csvList(row?.window_frequency_reference)));
        setSendTime(row ? secondsToTimeInput(row.send_report_time) : '09:00');
        setPdfSummary(row ? Boolean(row.pdf_summary) : true);
        setPdfDetails(Boolean(row?.pdf_details));
        setCsvDetails(Boolean(row?.csv_details));
        setEmails(csvList(row?.report_receiver));
        setNewEmailState('');
        setEmailError('');
        setSelectedUserIds(new Set(csvList(row?.report_user_ids)));
        setUserSearch('');
    }, []);

    /* Changing the rate clears the days: weekday indexes and days of the month share
       one field (window_frequency_reference) and mean nothing to each other. */
    const setFrequencyId = useCallback((id) => {
        setFrequencyIdState(id);
        setSelectedDays(new Set());
    }, []);

    const toggleDay = useCallback((day) => {
        setSelectedDays((prev) => {
            const next = new Set(prev);
            const val = String(day);
            if (next.has(val)) next.delete(val); else next.add(val);
            return next;
        });
    }, []);

    const setNewEmail = useCallback((v) => { setNewEmailState(v); setEmailError(''); }, []);

    const addEmail = useCallback(() => {
        const email = newEmail.trim().toLowerCase();
        if (!email) return;
        if (!EMAIL_PATTERN.test(email)) { setEmailError('Invalid email format'); return; }
        if (emails.includes(email)) { setEmailError('Email already added'); return; }
        setEmails((prev) => [...prev, email]);
        setNewEmailState('');
        setEmailError('');
    }, [newEmail, emails]);

    const removeEmail = useCallback((email) => {
        setEmails((prev) => prev.filter((e) => e !== email));
    }, []);

    /** Daily stores '1'; Weekly and Monthly store the picked days. */
    const freqRate = useMemo(
        () => (frequencyId === 1 ? ['1'] : [...selectedDays]),
        [frequencyId, selectedDays],
    );

    /** The payload fields both drawers send, in the stored format. */
    const payloadFields = useCallback(() => ({
        description,
        issue_freq_id: frequencyId,
        window_frequency_reference: freqRate.join(','),
        send_report_time: timeInputToSeconds(sendTime),
        pdf_summary: pdfSummary ? 1 : 0,
        pdf_details: pdfDetails ? 1 : 0,
        csv_details: csvDetails ? 1 : 0,
        report_receiver: emails.join(','),
    }), [description, frequencyId, freqRate, sendTime, pdfSummary, pdfDetails, csvDetails, emails]);

    return {
        description, setDescription,
        frequencyId, setFrequencyId, selectedDays, toggleDay, freqRate,
        sendTime, setSendTime,
        pdfSummary, setPdfSummary, pdfDetails, setPdfDetails, csvDetails, setCsvDetails,
        emails, newEmail, setNewEmail, emailError, addEmail, removeEmail,
        selectedUserIds, setSelectedUserIds, userSearch, setUserSearch,
        load, payloadFields,
    };
}

/**
 * Who may be added to or removed from User Recipients: only users you outrank.
 *
 * The edit drawer's page merges `user_role_weight` into its users. The create drawer is
 * opened from nine report pages whose users carry no weights, so it passes
 * `fetchWeights` and the weights are loaded on open. Until they arrive nobody is
 * manageable: iAmHigherAuthority fails closed on a missing weight, which is the right
 * default for a permission check.
 *
 * @returns {{ canManageUser: (user) => boolean, weightsLoaded: boolean }}
 */
export function useRecipientGate({ open, campusId, myRoleWeight, fetchWeights = false }) {
    const [userWeightById, setUserWeightById] = useState(fetchWeights ? null : {}); // null = loading
    useEffect(() => {
        if (!fetchWeights || !open || campusId == null) return undefined;
        let cancelled = false;
        setUserWeightById(null);
        /* Called inside .then so a synchronous throw becomes a rejection: fetchUsers
           builds its payload from the session (getGuid) and throws when it is gone,
           which would otherwise crash this drawer and the report page under it. */
        Promise.resolve()
            .then(() => fetchUsers(campusId))
            .then((rows) => {
                if (cancelled) return;
                const map = {};
                (Array.isArray(rows) ? rows : []).forEach((u) => {
                    if (u?.user_id != null) map[String(u.user_id)] = u.user_role_weight;
                });
                setUserWeightById(map);
            })
            .catch(() => { if (!cancelled) setUserWeightById({}); });
        return () => { cancelled = true; };
    }, [fetchWeights, open, campusId]);

    const weightsLoaded = userWeightById !== null;
    const canManageUser = useCallback(
        (user) => weightsLoaded
            && iAmHigherAuthority(userWeightById[String(user?.user_id)] ?? user?.user_role_weight, myRoleWeight),
        [weightsLoaded, userWeightById, myRoleWeight],
    );
    return { canManageUser, weightsLoaded };
}

/**
 * One save per click. A page's own setSaving(true) only disables the button on the next
 * render, so two clicks in one frame could both land (and five report pages passed
 * saving={false}, so a double-click created two schedules); a ref closes that window
 * synchronously. `busy` is true while either the page or the drawer says so.
 *
 * @returns {{ busy: boolean, submit: (fn: () => any) => void }}
 */
export function useSubmitGuard(saving) {
    const submittingRef = useRef(false);
    const [submitting, setSubmitting] = useState(false);
    const submit = useCallback((fn) => {
        if (submittingRef.current) return;
        submittingRef.current = true;
        setSubmitting(true);
        /* fn runs inside the chain so a synchronous throw still releases the guard
           rather than leaving the button disabled for good. */
        Promise.resolve()
            .then(fn)
            .catch((err) => console.error('[scheduleForm] save failed:', err))
            .finally(() => {
                submittingRef.current = false;
                setSubmitting(false);
            });
    }, []);
    return { busy: Boolean(saving) || submitting, submit };
}

/** The first field takes focus once the drawer has slid in. (Escape is the Drawer shell's.) */
export function useDrawerFocus(open, focusId) {
    useEffect(() => {
        if (!open) return undefined;
        const timer = setTimeout(() => document.getElementById(focusId)?.focus(), 200);
        return () => clearTimeout(timer);
    }, [open, focusId]);
}
